import { LargeHeadingStyled } from "../../../components/custom/texts/texts.styled";
import { Button, Card, Drawer, Dropdown, Flex, Form, Icon, Select } from "@nubeteck/components";
import { ArrayUtils } from "@nubeteck/utils";
import { Input, Layout, Table, Skeleton } from "antd";
import { StrapiError } from "../../StrapiError";
import { ColumnsType } from "antd/es/table";
import React, { useState, useEffect } from "react";
import { FormInstance } from "antd/lib/form";
import { useGetRolesQuery } from "src/services";
import {
  useErrorSavingDataToast,
  useErrorUpdatingUserStateToast,
  useSuccessUpdateToast,
  useSuccessRegisterToast,
} from "src/constants";
import {
  useChangeUserStateMutation,
  useCreateUserMutation,
  useGetUsersMutation,
  useUpdateUserMutation,
} from "src/services/userServices";
import { getSettingsPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { _translateValue } from "../translation-gestor/translateValue";

const { Content } = Layout;
const SCREEN_NAME = "PantallaUsers";

interface IUser {
  userId: number;
  isActive: true;
  fullName: string;
  username: string;
  email: string;
  roles: number[];
  rolesString?: string;
}

const Users: React.FC = () => {
  const [form] = Form.useForm<IUser>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [usuarioId, setUsuarioId] = React.useState(0);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const { locale } = useLocale();
  const [settingsPage, setSettingsPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const showSuccessToast = useSuccessRegisterToast();
  const [translatedData, setTranslatedData] = React.useState<{
    roles: { [key: string]: string };
    estados: { [key: string]: string };
  }>({
    roles: {},
    estados: {},
  });
  const showErrorSavingToast = useErrorSavingDataToast();
  const showErrorUpdatingUserStateToast = useErrorUpdatingUserStateToast();
  const showSuccessUpdateToast = useSuccessUpdateToast();

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSettingsPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getSettingsPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const [userFiltro, setUserFiltro] = React.useState<string>("");
  const formRef = React.useRef<FormInstance>(null);

  const { data: roles } = useGetRolesQuery("");
  const [getUsers, { data, isLoading }] = useGetUsersMutation();
  const [
    createUser,
    { isLoading: isLoadingCreate, isSuccess: isSuccessCreate, isError: isErrorCreate },
  ] = useCreateUserMutation();
  const [
    updateDate,
    { isLoading: isLoadingUpdate, isSuccess: isSuccessUpdate, isError: isErrorUpdate },
  ] = useUpdateUserMutation();
  const [
    changeUserState,
    {
      isLoading: isLoadingState,
      isSuccess: isSuccessState,
      isError: isErrorState,
      error: errorState,
    },
  ] = useChangeUserStateMutation();

  React.useEffect(() => {
    getUsers(userFiltro);
  }, [
    getUsers,
    isSuccessCreate,
    isSuccessUpdate,
    isErrorCreate,
    isErrorUpdate,
    isSuccessState,
    userFiltro,
  ]);

  React.useEffect(() => {
    setDrawerOpen(false);
    if (isErrorCreate) showErrorSavingToast();

    if (isSuccessCreate) {
      showSuccessToast();
      form.resetFields();
      setUsuarioId(0);
    }
  }, [isSuccessCreate, form, isErrorCreate, showSuccessToast, showErrorSavingToast]);

  React.useEffect(() => {
    setDrawerOpen(false);
    if (isErrorUpdate) showErrorSavingToast();

    if (isSuccessUpdate) {
      showSuccessToast();
      form.resetFields();
      setUsuarioId(0);
    }
  }, [isSuccessUpdate, form, isErrorUpdate, showSuccessToast, showErrorSavingToast]);

  React.useEffect(() => {
    if (isErrorState) {
      showErrorUpdatingUserStateToast();
    }

    if (isSuccessState) {
      showSuccessUpdateToast();
    }
  }, [errorState, isSuccessState, isErrorState, showErrorUpdatingUserStateToast, showSuccessUpdateToast]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      const hasData = data?.data && Array.isArray(data.data) && data.data.length > 0;
      const hasRoles = roles?.data && Array.isArray(roles.data) && roles.data.length > 0;

      if (!hasData && !hasRoles) {
        setTranslatedData({ roles: {}, estados: {} });
        return;
      }

      const rolesTranslations: { [key: string]: string | null } = {};
      const estadosTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      // Traducir roles del Select
      if (hasRoles) {
        roles.data.forEach((r: any) => {
          if (r.rolName && !rolesTranslations.hasOwnProperty(r.rolName)) {
            rolesTranslations[r.rolName] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, r.rolName, locale).then((translated) => {
                rolesTranslations[r.rolName] = translated;
              })
            );
          }
        });
      }

      // Traducir roles de la tabla (rolesString puede contener múltiples roles separados)
      if (hasData) {
        data.data.forEach((record: IUser) => {
          if (record.rolesString) {
            // rolesString puede ser una cadena separada por comas o algún otro delimitador
            // Dividir por comas y espacios para obtener los roles individuales
            const rolesArray = record.rolesString.split(/[,;]/).map((r: string) => r.trim()).filter((r: string) => r);
            rolesArray.forEach((rolName: string) => {
              if (rolName && !rolesTranslations.hasOwnProperty(rolName)) {
                rolesTranslations[rolName] = null;
                translationPromises.push(
                  _translateValue(SCREEN_NAME, rolName, locale).then((translated) => {
                    rolesTranslations[rolName] = translated;
                  })
                );
              }
            });
          }
          // Traducir estado (Activo/Inactivo)
          const estadoText = record.isActive ? "Activo" : "Inactivo";
          if (!estadosTranslations.hasOwnProperty(estadoText)) {
            estadosTranslations[estadoText] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, estadoText, locale).then((translated) => {
                estadosTranslations[estadoText] = translated;
              })
            );
          }
        });
      }

      await Promise.all(translationPromises);
      setTranslatedData({
        roles: rolesTranslations as { [key: string]: string },
        estados: estadosTranslations as { [key: string]: string },
      });
    };
    translateData();
  }, [data?.data, roles?.data, locale]);

  const onClickEditarUsuario = React.useCallback(
    (record: IUser) => {
      setUsuarioId(record.userId);
      form.setFieldsValue({
        ...record,
      });
      setDrawerOpen(true);
    },
    [form],
  );

  const cambiarEstadoUsuario = React.useCallback(
    (value: boolean, id: number) => {
      changeUserState({ isActive: value, id });
    },
    [changeUserState],
  );

  const submit = (values: any) => {
    const v = {
      ...values,
    };
    if (usuarioId !== 0) {
      updateDate({
        ...v,
        id: usuarioId,
      });
    } else {
      createUser(v);
    }
  };

  const rolesOptions = React.useMemo(() => {
    return (roles?.data ?? []).map((r: any) => ({
      label: translatedData.roles[r.rolName] || r.rolName,
      value: r.rolId,
    }));
  }, [roles?.data, translatedData.roles]);

  const columns = React.useMemo<ColumnsType<IUser>>(
    () => [
      {
        title: settingsPage?.user_setting_table?.[0]?.label || "Usuario",
        dataIndex: "username",
      },
      {
        title: settingsPage?.user_setting_table?.[1]?.label || "Nombre",
        dataIndex: "fullName",
      },
      {
        title: settingsPage?.user_setting_table?.[2]?.label || "Correo electrónico",
        dataIndex: "email",
      },
      {
        title: settingsPage?.user_setting_table?.[3]?.label || "Roles",
        dataIndex: "rolesString",
        render: (text: string) => {
          if (!text) return "";
          // Dividir rolesString por comas o punto y coma y traducir cada uno
          const rolesArray = text.split(/[,;]/).map((r: string) => r.trim()).filter((r: string) => r);
          return rolesArray.map((rolName: string) => translatedData.roles[rolName] || rolName).join(", ");
        },
      },
      {
        title: settingsPage?.user_setting_table?.[4]?.label || "Estado",
        dataIndex: "isActive",
        render: (_: boolean, record: IUser) => {
          const estadoText = record.isActive ? "Activo" : "Inactivo";
          return translatedData.estados[estadoText] || estadoText;
        },
      },
      {
        title: "",
        dataIndex: "actions",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Dropdown
            overlayClassName="table-dropdown"
            className="cursor-pointer"
            key={record.userId}
            trigger={["click"]}
            align={{ offset: [-40, 4] }}
            menu={{
              items: [
                {
                  key: "1",
                  label: settingsPage?.edit_button || "Editar",
                  icon: <Icon name="" color="#A7A7A7" />,
                  onClick: () => onClickEditarUsuario(record),
                },
                {
                  key: "2",
                  label: record.isActive ? (settingsPage?.inactive_button || "Inactivar") : (settingsPage?.active_button || "Activar"),
                  icon: <Icon name={record.isActive ? "" : ""} color="#A7A7A7" />,
                  onClick: () => cambiarEstadoUsuario(!record.isActive, record.userId),
                },
              ],
            }}
          >
            <Icon name="" isTouchable />
          </Dropdown>
        ),
      },
    ],
    [onClickEditarUsuario, cambiarEstadoUsuario, settingsPage, translatedData],
  );

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setSettingsPage(null);
      }}
    />;
  }

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      <Content style={{ marginTop: "16px" }}>
        {isLoadingContent ? (
          <>
            <Flex style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
              <Skeleton.Input active size="large" style={{ width: 200 }} />
              <Flex style={{ gap: "15px" }}>
                <Skeleton.Input active size="default" style={{ width: 200 }} />
                <Skeleton.Input active size="default" style={{ width: 150 }} />
              </Flex>
            </Flex>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          </>
        ) : (
          <>
            <Flex style={{ width: "100%", justifyContent: "space-between" }}>
              <LargeHeadingStyled>{settingsPage?.user__nb || "Usuarios"}</LargeHeadingStyled>
              <Flex style={{ gap: "15px" }}>
                <Form.Item label={settingsPage?.search_input || "Búsqueda por nombre"}>
                  <Input
                    type="text"
                    size="small"
                    defaultValue={userFiltro}
                    onChange={(e) => setUserFiltro(e.target.value)}
                    placeholder={settingsPage?.search_spaceholder || "Nombre"}
                    style={{ height: "35px", marginRight: "15px" }}
                  />
                </Form.Item>
                <Button
                  title=""
                  type="primary"
                  onClick={() => {
                    setDrawerOpen(true);
                    form.resetFields();
                  }}
                >
                  {settingsPage?.user_detail?.user_new_button || "Nuevo usuario"}
                </Button>
              </Flex>
            </Flex>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Table<IUser>
                loading={isLoading || isLoadingCreate || isLoadingUpdate || isLoadingState}
                columns={columns}
                dataSource={data?.data}
                rowKey={(v) => v.userId}
                pagination={{
                  defaultPageSize: 5,
                  showSizeChanger: true,
                  style: { marginRight: "30px" },
                }}
                scroll={{ x: 800 }}
              />
              <Drawer
                placement="right"
                title={usuarioId !== 0 ? (settingsPage?.user_detail?.title || "Edición de usuario") : (settingsPage?.user_detail?.user_new_button || "Nuevo usuario")}
                open={drawerOpen}
                onClose={() => {
                  setUsuarioId(0);
                  setDrawerOpen(false);
                }}
              >
            {" "}
            <Form
              form={form}
              layout="vertical"
              ref={formRef}
              onFinish={submit}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
              initialValues={{
                id: 1,
              }}
            >
              <Form.Item
                name="fullName"
                label={settingsPage?.user_detail?.name || "Nombre"}
                hasFeedback
                required
                rules={[
                  {
                    required: true,
                    message: settingsPage?.required_field_message || "Este campo es requerido.",
                  },
                ]}
              >
                <Input type="text" />
              </Form.Item>
              <Form.Item
                name="email"
                label={settingsPage?.user_detail?.email || "Correo electrónico"}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: settingsPage?.required_field_message || "Este campo es requerido.",
                  },
                ]}
              >
                <Input type="email" />
              </Form.Item>
              <Form.Item
                name="roles"
                label={settingsPage?.user_detail?.role || "Roles"}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: settingsPage?.required_field_message || "Este campo es requerido.",
                  },
                ]}
              >
                <Select
                  allowClear
                  size="large"
                  options={rolesOptions}
                  mode="multiple"
                  placeholder={settingsPage?.user_detail?.role || "Roles del usuario"}
                />
              </Form.Item>

              <Button
                type="primary"
                size="large"
                htmlType="submit"
                style={{ alignSelf: "flex-end" }}
                loading={isLoadingCreate || isLoadingState || isLoadingUpdate}
              >
                {settingsPage?.user_detail?.save_button || "Guardar"}
              </Button>
            </Form>
          </Drawer>
            </Card>
          </>
        )}
      </Content>
    </Flex>
  );
};

export default Users;
